import { Box, Field as ChakraField, defineStyle } from '@chakra-ui/react'
import * as React from 'react'

export interface FieldProps extends Omit<ChakraField.RootProps, 'label'> {
  label?: React.ReactNode
  helperText?: React.ReactNode
  errorText?: React.ReactNode
  optionalText?: React.ReactNode
  bgColor?: string
  isLongLabel?: boolean
  size?: 'sm' | 'md'
}

export const CField = React.forwardRef<HTMLDivElement, FieldProps>(
  function Field(props, ref) {
    const {
      label,
      children,
      helperText,
      errorText,
      optionalText,
      isLongLabel = false,
      bgColor,
      size = 'md',
      ...rest
    } = props

    const height = size === 'sm' ? '49px' : '56px'
    const placeholderTop = size === 'sm' ? '16px' : '19.5px'
    const labelTop = isLongLabel ? '8px' : '12px'

    return (
      <ChakraField.Root ref={ref} {...rest}>
        <Box
          w='full'
          pos='relative'
          css={{
            '& > input': {
              px: 4,
              pt: isLongLabel ? '32px' : '28px',
              pb: isLongLabel ? '8px' : '12px',
              lineHeight: 1.2,
              fontSize: '16px',
              height,
              width: '100%',
              rounded: '2xl',
              bgColor: bgColor || 'background',
              border: 'none',
            },
          }}
        >
          {children}
          {label && (
            <ChakraField.Label
              lineHeight={1}
              css={floatingStyles({ placeholderTop, labelTop })}
            >
              {label}
              <ChakraField.RequiredIndicator fallback={optionalText} />
            </ChakraField.Label>
          )}
        </Box>
        {helperText && (
          <ChakraField.HelperText>{helperText}</ChakraField.HelperText>
        )}
        {errorText && (
          <ChakraField.ErrorText>{errorText}</ChakraField.ErrorText>
        )}
      </ChakraField.Root>
    )
  }
)

const floatingStyles = ({
  placeholderTop,
  labelTop,
}: {
  placeholderTop: string
  labelTop: string
}) =>
  defineStyle({
    pos: 'absolute',
    top: labelTop,
    color: 'var(--text-muted)',
    insetStart: '4',
    fontSize: '12px',
    fontWeight: 'normal',
    pointerEvents: 'none',
    transition: 'position',
    _peerPlaceholderShown: {
      color: 'var(--text-muted)',
      top: placeholderTop, // Dynamic top value
      insetStart: '4',
      fontSize: '16px',
    },
    _peerFocusVisible: {
      color: 'var(--text-muted)',
      top: '12px',
      insetStart: '4',
      fontSize: '12px',
    },
  })
